import type * as ents from '@attain-apps/entities'
import * as store from '@attain-apps/fe/web/main/lib/store'

import type { Api } from './api'
import { State } from './state'

/**
 * Core actions for this feature
 */
// NOTE: intentionally missing return type so that type can be inferred
export function getActionsConfig() {
  return {
    /** Load functionalizations for the current joint-use target and scenario. */
    loadForCurrentEntity: store.actions.request({
      input: store.actions.Data.NONE,
      output: store.actions.Data.asIs<ents.extra.space.Functionalization[]>(),
      reducers: {
        request: () => ({
          ...State.INITIAL,
          isLoading: true,
        }),
        success: ({ state, data: { response } }) => ({
          ...state,
          error: undefined,
          functionalizations: response,
          isLoading: false,
        }),
        error: ({ state, data: { error } }) => ({
          ...state,
          error,
          isLoading: false,
        }),
        cancellation: () => State.INITIAL,
      },
    }),
    /** Set the plugin-owned current organization target. */
    setCurrentEntityTarget: store.actions.request({
      input: store.actions.Data.asIs<{
        readonly id: ents.extra.space.Organization.Id
      }>(),
      output: store.actions.Data.NONE,
      reducers: {
        request: () => ({
          ...State.INITIAL,
          isLoading: true,
        }),
        error: ({ state, data: { error } }) => ({
          ...state,
          error,
          isLoading: false,
        }),
      },
    }),
    /**
     * Release the plugin-owned current organization target.
     *
     * NOTE: This plugin intentionally uses one shared current-entity target. If
     * organization editing and occupier functional-usage ever need concurrent
     * joint-use loads, move this workflow to target-keyed state instead of
     * relying on a global release.
     */
    releaseCurrentEntityTarget: store.actions.request({
      input: store.actions.Data.NONE,
      output: store.actions.Data.NONE,
      reducers: {
        request: () => State.INITIAL,
        success: ({ state }) => ({
          ...state,
          error: undefined,
        }),
        error: ({ state, data: { error } }) => ({
          ...state,
          error,
        }),
        cancellation: ({ state }) => ({
          ...state,
          error: undefined,
        }),
      },
    }),
    /** Update the edited organization's joint-use calculation mode. */
    updateCalculationMode: store.actions.standalone({
      data: store.actions.Data.asIs<{
        calculationMode: ents.extra.space.Organization.JointUseCalculationMode
      }>(),
    }),
    /** Update one manual joint-use functionalization. */
    updateManualFunctionalization: store.actions.standalone({
      data: store.actions.Data.asIs<ents.extra.space.Functionalization>(),
    }),
    /** Transport-only request for joint-use functionalizations. */
    load: store.actions.request({
      input: store.actions.Data.asIs<{
        context: Api.Get.Context
        params: Api.Get.Params
      }>(),
      output: store.actions.Data.asIs<ents.extra.space.Functionalization[]>(),
    }),
  } as const satisfies store.actions.Config.Constraint<State>
}
